// Small helpers for building and finding page elements.
import { Directory, Filesystem, Share, isNative } from "./native.ts";

/** The element matching `selector`. The page's markup is fixed, so a missing element is a bug: this throws. */
export function $<T extends HTMLElement = HTMLElement>(selector: string, root: ParentNode = document): T {
  const n = root.querySelector<T>(selector);
  if (!n) throw new Error("Missing element: " + selector);
  return n;
}
/** Like $, for elements that may not be there (created later, or only on some screens). */
export const $maybe = <T extends HTMLElement = HTMLElement>(selector: string, root: ParentNode = document): T | null =>
  root.querySelector<T>(selector);

export const input = (selector: string) => $<HTMLInputElement>(selector);

type Handler<E extends HTMLElement> = (e: Event & { currentTarget: E }) => unknown;
type AttrValue = string | number | boolean | null | undefined;
/** Attributes for el(): plain values become attributes; `class`, `text` and on… handlers are special. */
export type Attrs<E extends HTMLElement> = {
  class?: string;
  text?: string;
  onclick?: Handler<E>;
  onchange?: Handler<E>;
  oninput?: Handler<E>;
} & { [name: string]: AttrValue | Handler<E> };
export type Child = Node | string | null | undefined | false;

/** Creates an element: el("button", { class: "btn", onclick }, icon("plus"), "Add"). */
export function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs<HTMLElementTagNameMap[K]> = {}, ...kids: (Child | Child[])[]): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === "class") n.className = String(v);
    else if (k === "text") n.textContent = String(v);
    else if (typeof v === "function") n.addEventListener(k.slice(2), v as EventListener);
    else n.setAttribute(k, v === true ? "" : String(v));
  }
  for (const k of kids.flat()) if (k != null && k !== false) n.append(k);
  return n;
}

/** One of the SVG symbols defined at the top of index.html (#i-<name>). */
export function icon(name: string): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
  use.setAttribute("href", "#i-" + name);
  svg.append(use);
  svg.setAttribute("aria-hidden", "true");
  return svg;
}

let toastTimer = 0;
/** A short message at the bottom of the screen. Longer texts stay longer. */
export function toast(msg: string): void {
  const box = $("#toast");
  box.textContent = msg;
  box.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { box.hidden = true; }, Math.min(9000, 2200 + msg.length * 45));
}

/** Two taps to confirm (delete and the like): the first tap changes the label to `confirmText`, the second runs `onConfirm`. */
export function twoTap(btn: HTMLButtonElement, onConfirm: () => unknown, label: () => string, confirmText: () => string): HTMLButtonElement {
  let armed = false, timer = 0;
  const reset = () => { armed = false; btn.classList.remove("armed"); btn.textContent = label(); };
  btn.addEventListener("click", () => {
    if (!armed) { armed = true; btn.classList.add("armed"); btn.textContent = confirmText(); timer = window.setTimeout(reset, 3500); return; }
    clearTimeout(timer); reset(); onConfirm();
  });
  return btn;
}

/** Saves a file: the share sheet on phones (so it can go to Drive, mail...), a download elsewhere. */
export async function saveFile(blob: Blob, name: string, title: string): Promise<"shared" | "downloaded" | "cancelled"> {
  if (isNative()) {
    // the Android app's web view can't download or use the web share sheet: write the file, then share it natively
    const data = await new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1] ?? ""); r.onerror = rej; r.readAsDataURL(blob); });
    const { uri } = await Filesystem.writeFile({ path: name, data, directory: Directory.Cache });
    try { await Share.share({ title, files: [uri] }); return "shared"; }
    catch { return "cancelled"; }
  }
  const file = new File([blob], name, { type: blob.type });
  if (navigator.canShare?.({ files: [file] }) && /Android|iPhone|iPad/i.test(navigator.userAgent)) {
    try { await navigator.share({ files: [file], title }); return "shared"; }
    catch (e) { if (e instanceof DOMException && e.name === "AbortError") return "cancelled"; }
  }
  const a = el("a", { href: URL.createObjectURL(blob), download: name });
  document.body.append(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 10000);
  return "downloaded";
}
